import { Button, Grid, Link, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useHistory } from "react-router-dom";
import { ReactComponent as BranchOfficeDepositsImage } from "../../common/assets/images/BranchOffices_Static.svg";
import { ReactComponent as CustomDataDownload } from "../../common/assets/images/CustomDataDownload.svg";
import { ReactComponent as MarketShare } from "../../common/assets/images/MarketShare.svg";
import { ReactComponent as SummaryTables } from "../../common/assets/images/SummaryTables.svg";
import { ReactComponent as SavedPeerGroups } from "../../common/assets/images/SavePeersReports.svg";
import { appHeaderTitle } from "../../common/services/Utilities";
import { AppUrl } from "../../config";
import Breadcrumbs from "../../containers/Breadcrumbs";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import { AppBody } from "../AppBody";
import HelpButton from "../HelpContent/HelpButton";

const useStyle = makeStyles(theme => ({
  titles: { marginTop: 16 },
  button: {
    "display": "block",
    "height": 330,
    "margin": "0 auto",
    "&:hover, &.Mui-focusVisible": {
      "backgroundColor": "transparent",
      "& #Rectangle2,": {
        stroke: theme.palette.secondary.main,
        fill: theme.palette.secondary.main,
      },
      "& #Combined-Shape2, & #Frame_Static": {
        fill: "#a58922",
        stroke: "#a58922",
      },
      "& #Line1, & #Line2": {
        stroke: "#4b699a",
        fill: "4b699a",
      },
      "& #Rectangle1": {
        stroke: "#a58922",
        fill: theme.palette.primary.main,
      },
      "& #Combined-Shape3, & #Combined-Shape4 ": { fill: "#ffffff" },
      "& #Oval5, & #Oval1, & #Oval3, & #Oval7, & #Oval5, & #Oval9, & #d1, & #d2, & #d3, & #d4, & #d5": {
        fill: "#ffffff",
      },
      "& #Oval6, & #Oval2,  & #Oval4,  & #Oval8, & #Oval10, & #Oval11, & #Bitmap, & #Combined-Shape-DownArrow, & #Group-5, & #Checkbox-Area, & #d6, & #d7": {
        fill: theme.palette.primary.main,
      },
      "& #Combined-Shape2": { fill: "#a58922" },
      "& #Group-6, & #Rectangle-ManageSPG": {
        fill: "#ffffff",
      },
      "& .Rectangle-ManageSPG-Copy, & #Path-3": {
        stroke: "#ffffff",
        fill: theme.palette.primary.main,
      },
      "& #Rectangle-Download": {
        fill: "#ffffff",
      },
      "& #Rectangle": {
        fill: theme.palette.primary.main,
        stroke: "#a58922",
      },
      "& #Combined-Shape-Checks": {
        fill: "#c6d0e0",
        stroke: "#c6d0e0",
      },
      "& #USMap": {
        fill: "#4b699a",
      },
    },
  },
}));

export default function SOD() {
  const classes = useStyle();
  const history = useHistory();

  useInitialPageLoad("SOD");

  const introParagraph = (
    <>
      {appHeaderTitle("SOD")}
      <Typography variant="body2" id="peer-group-description">
        The annual Summary of Deposits (SOD) survey data provides a number of
        opportunities to better understand the status of our banking system.
        With the holistic aggregations of the Summary Tables, the geographically
        sensitive Market Share Reports, and the institution-centric Branch
        Office Deposits, you will be able to see a clear view of where the
        deposits are and how they’ve changed over time.
      </Typography>
      <Typography variant="body2" id="peer-group-feedback-link">
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=SOD"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
    </>
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={11}>
          <Breadcrumbs currentPageBreadCrumbTitle="Summary of Deposits" />
        </Grid>
        <Grid item xs={1}>
          <HelpButton />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody introText={introParagraph}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Button
              id="summaryTables"
              className={classes.button}
              onClick={() =>
                history.push({
                  pathname: `${AppUrl("SOD")}/summaryTables`,
                  state: { url: `${AppUrl("SOD")}` },
                })
              }
            >
              <SummaryTables />
              <Typography variant="h2" className={classes.titles}>
                Summary<br></br>Tables
              </Typography>
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              id="marketShare"
              className={classes.button}
              onClick={() =>
                history.push({
                  pathname: `${AppUrl("SOD")}/marketShare`,
                  state: { url: `${AppUrl("SOD")}` },
                })
              }
            >
              <MarketShare />
              <Typography variant="h2" className={classes.titles}>
                Market Share<br></br>Reports
              </Typography>
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              id="branchOfficeDeposits"
              className={classes.button}
              onClick={() =>
                history.push({
                  pathname: `${AppUrl("SOD")}/branchOffice`,
                  state: { url: `${AppUrl("SOD")}` },
                })
              }
            >
              <BranchOfficeDepositsImage />
              <Typography variant="h2" className={classes.titles}>
                Branch Office<br></br>Deposits
              </Typography>
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              id="sodCustomDataDownload"
              className={classes.button}
              onClick={() =>
                history.push({
                  pathname: `${AppUrl("SOD")}/customDownload`,
                  state: { url: `${AppUrl("SOD")}` },
                })
              }
            >
              <CustomDataDownload />
              <Typography variant="h2" className={classes.titles}>
                Custom Data<br></br>Download
              </Typography>
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              id="sodManageSavedContent"
              className={classes.button}
              onClick={() =>
                history.push({
                  pathname: `${AppUrl("PeerGroup")}/manage`,
                  state: {
                    url: AppUrl("SOD") + window.location.search,
                  },
                })
              }
            >
              <SavedPeerGroups />
              <Typography variant="h2">
                Manage Your Saved<br></br>Custom Content
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </AppBody>
    </>
  );
}
